/**
 * Backwards-compat shim — actual implementation lives in
 * `@homerunner-next/widget-core/utils`.
 */
import { getProxiedImageUrl } from "@homerunner-next/widget-core/utils";

export { getProxiedImageUrl };

/**
 * The proxy host every getProxiedImageUrl call emits its output onto. Scheme
 * and host are matched case-insensitively and `http://` counts too, the same
 * test the parser applies (#1564 review: `http://hrimgs.co/...` and
 * `https://HRIMGS.CO/...` are working proxy URLs). The trailing `/` is
 * load-bearing: after `://` only `/`, `?` and `#` end the authority, so
 * `hrimgs.co.evil.com`, `hrimgs.co@evil.com` and `hrimgs.co:443` all fail
 * this test and take the normal proxy path.
 */
const PROXY_URL_RE = /^https?:\/\/hrimgs\.co\//i;

/**
 * getProxiedImageUrl, but a no-op on a URL that is already on the proxy
 * host. dashboard#1519: parseBedroomImages (common/bedroom-showcase.ts)
 * unwraps a pasted `plain/`-form hrimgs.co URL to its original, so the
 * normal proxy call applies with the size this call site needs; what still
 * reaches here on the proxy host is imgproxy's signed or base64 form, which
 * carries no readable original. Proxying that again nests a second
 * `https://hrimgs.co/.../plain/` inside the first, which imgproxy cannot
 * resolve and renders as its gray placeholder, so it passes through as
 * pasted (at whatever rendition the operator pasted). Scoped to the one call
 * site that needs it rather than changed in getProxiedImageUrl itself: every
 * other caller in this package proxies a Central-controlled image value that
 * is never already-proxied, so this stays a local guard instead of a
 * behavior change on the shared widget-core helper every widget calls.
 */
export function getProxiedImageUrlOnce(
  imageUrl: string,
  width?: number,
  height?: number,
): string {
  if (PROXY_URL_RE.test(imageUrl)) return imageUrl;
  return getProxiedImageUrl(imageUrl, width, height);
}
